import {isFeatureEnabled} from '@github-ui/feature-flags'

/**
 * IMPORTANT NOTICE: Feature flags added here must also be added to the `packages/feature-flags/client-feature-flags.ts` file
 * or they will not be available to check in this file!
 */

class CopilotFeatureFlags {
  get immersiveFigmaIntegration() {
    return isFeatureEnabled('copilot_immersive_figma_integration')
  }

  get planningAgent() {
    return isFeatureEnabled('copilot_immersive_planning_agent')
  }

  get draftIssueProjectsWithID() {
    return isFeatureEnabled('copilot_api_draft_issue_reference_with_project_id')
  }

  get draftIssueUseRefForDraftBlock() {
    return isFeatureEnabled('copilot_draft_issue_use_ref_for_draft_block')
  }

  get focusTextAreaInsteadOfWorkbench() {
    return isFeatureEnabled('copilot_focus_text_area_instead_of_workbench')
  }

  get forceLegacyChatDefaultModel() {
    return isFeatureEnabled('copilot_api_force_legacy_base_chat_model')
  }

  get draftUpdateIssue() {
    return isFeatureEnabled('copilot_api_github_draft_update_issue_skill')
  }

  get domPageContext() {
    return isFeatureEnabled('copilot_use_dom_page_context')
  }

  /**
   * When enabled, the interview survey dialog replaces the 'Give feedback' dialog in copilot chat.
   * @returns {boolean} Whether the feature is enabled.
   */
  get copilotChatInterviewSurvey() {
    return isFeatureEnabled('copilot_chat_interview_survey')
  }

  get attachMultipleImages() {
    return isFeatureEnabled('copilot_chat_attach_multiple_images')
  }

  get clearModelSelectionForDefaultChange() {
    return isFeatureEnabled('copilot_chat_clear_model_selection_for_default_change')
  }

  get enableToolCallLogs() {
    return isFeatureEnabled('copilot_chat_enable_tool_call_logs')
  }

  /** Feature flag for text selection attachments in file previews */
  get selectionAttachments() {
    return isFeatureEnabled('copilot_chat_selection_attachments')
  }

  get customCopilots() {
    return isFeatureEnabled('copilot_custom_copilots')
  }

  get immersiveStructuredModelPicker() {
    return isFeatureEnabled('copilot_immersive_structured_model_picker')
  }

  get customCopilotsFeaturePreview() {
    return isFeatureEnabled('copilot_custom_copilots_feature_preview')
  }

  get clientDOMSkills() {
    return isFeatureEnabled('copilot_client_dom_skills')
  }

  get dotcomChatClientSideSkills() {
    return isFeatureEnabled('dotcom_chat_client_side_skills')
  }

  get loopsPlugin() {
    return isFeatureEnabled('copilot_pipes')
  }

  get workbenchPlugin() {
    return isFeatureEnabled('copilot_workbench')
  }

  get copilotChatOpeningThreadSwitch() {
    return isFeatureEnabled('copilot_chat_opening_thread_switch')
  }

  get copilotDuplicateThread() {
    return isFeatureEnabled('copilot_duplicate_thread')
  }

  get copilotShareActiveSubthread() {
    return isFeatureEnabled('copilot_share_active_subthread')
  }

  get copilotChatO1Tools() {
    return isFeatureEnabled('copilot_api_tools_for_non_streaming_models')
  }

  get showDeepCodeSearchButton() {
    return (
      isFeatureEnabled('copilot_show_deep_code_search_button') && isFeatureEnabled('copilot_api_search_agent_skill')
    )
  }

  get deleteAllConversations() {
    return isFeatureEnabled('copilot_delete_all_conversations')
  }

  get staffPromptDialog() {
    return isFeatureEnabled('copilot_staff_prompt_dialog')
  }

  get staffToolsMenu() {
    return isFeatureEnabled('copilot_staff_tools_menu')
  }

  get workbenchTerminal() {
    return isFeatureEnabled('copilot_workbench_terminal')
  }

  get workbenchPreviewAnalytics() {
    return isFeatureEnabled('copilot_workbench_preview_analytics')
  }

  get visionAllowedInClaude() {
    return isFeatureEnabled('copilot_chat_vision_in_claude')
  }

  get premiumRequestQuotasEnabled() {
    return isFeatureEnabled('copilot_premium_request_quotas')
  }

  get freeToPaidTelemetry() {
    return isFeatureEnabled('copilot_free_to_paid_telem')
  }

  get freeToPaidSettingsUpgrade() {
    return isFeatureEnabled('copilot_ftp_settings_upgrade')
  }

  get freeToPaidYourCopilotSettings() {
    return isFeatureEnabled('copilot_ftp_your_copilot_settings')
  }

  get freeToPaidUpgradeToProFromModels() {
    return isFeatureEnabled('copilot_ftp_upgrade_to_pro_from_models')
  }

  get spacesImagesEnabled() {
    return isFeatureEnabled('copilot_custom_copilots_images')
  }

  get immersiveJobResultPreview() {
    return isFeatureEnabled('copilot_immersive_job_result_preview')
  }

  get copilotPersistEditedDraftIssues() {
    return isFeatureEnabled('copilot_immersive_draft_issue_persist_edited_issues_in_session')
  }

  get copilotThirdPartyMcp() {
    return isFeatureEnabled('copilot_api_dotcom_chat_use_third_party_mcp_servers')
  }

  get disableModelPickerWhileStreaming() {
    return isFeatureEnabled('copilot_chat_disable_model_picker_while_streaming')
  }

  get sparkUseSeparateSuggestionArgs() {
    return isFeatureEnabled('copilot_spark_use_separate_suggestion_args')
  }

  get freeToPaidHyperspaceUpgradePrompt() {
    return isFeatureEnabled('copilot_ftp_hyperspace_upgrade_prompt')
  }

  get reduceChatQuotaChecks() {
    return isFeatureEnabled('copilot_chat_reduce_quota_checks')
  }

  get requestTracing() {
    return isFeatureEnabled('copilot_api_staff_only_tracing')
  }

  get coffeTraceViewer() {
    return isFeatureEnabled('copilot_coffe_trace_view_enabled')
  }

  get copilotSpacesReadAccessToUserOwnedSpaces() {
    return isFeatureEnabled('copilot_spaces_read_access_to_user_owned_spaces')
  }

  get copilotSpacesPublicAccessToUserOwnedSpaces() {
    return isFeatureEnabled('copilot_spaces_public_access_to_user_owned_spaces')
  }

  get copilotSpacesReportAbuse() {
    return isFeatureEnabled('copilot_spaces_report_abuse')
  }

  get copilotSpacesServerSideMenuActions() {
    return isFeatureEnabled('copilot_spaces_server_side_menu_actions')
  }

  get capiErrorResponseTelemetry() {
    return isFeatureEnabled('copilot_capi_error_response_telemetry')
  }

  get sparkUseNewDataLayer() {
    return isFeatureEnabled('copilot_spark_new_data_layer')
  }

  get sparkLoadingWebGL() {
    return isFeatureEnabled('copilot_spark_loading_webgl')
  }

  get fileBlockRefMatching() {
    return isFeatureEnabled('copilot_file_block_ref_matching')
  }

  get stableConversationView() {
    return isFeatureEnabled('copilot_stable_conversation_view')
  }

  get copilotRedirectDiffViewToImmersive() {
    return isFeatureEnabled('copilot_chat_diff_view_redirect') && !isFeatureEnabled('copilot_immersive_embedded')
  }

  get copilotRedirectFileViewToImmersive() {
    return isFeatureEnabled('copilot_chat_file_redirect') && !isFeatureEnabled('copilot_immersive_embedded')
  }

  get copilotRedirectSnippetToImmersive() {
    return isFeatureEnabled('copilot_chat_snippet_redirect') && !isFeatureEnabled('copilot_immersive_embedded')
  }

  get mcpToolDescriptions() {
    return isFeatureEnabled('copilot_mcp_tool_descriptions')
  }

  get immersiveEmbedded() {
    return isFeatureEnabled('copilot_immersive_embedded')
  }

  get copilotResourcePanel() {
    return isFeatureEnabled('copilot_resource_panel')
  }

  get spacesAsAttachmentsEnabled() {
    return isFeatureEnabled('copilot_spaces_as_attachments')
  }

  get spacesIndividualPoliciesEnabled() {
    return isFeatureEnabled('copilot_spaces_individual_policies_ga')
  }

  get loopExecutionReferences() {
    return isFeatureEnabled('loops_execution_references')
  }

  get loopsAbusePrevention() {
    return isFeatureEnabled('loops_abuse_prevention')
  }

  get copilotWorkbenchShowPreviewTool() {
    return isFeatureEnabled('copilot_workbench_show_preview_tool')
  }

  get sweAgentUseSubagents() {
    return isFeatureEnabled('copilot_swe_agent_use_subagents')
  }

  get chatInputCommands() {
    return isFeatureEnabled('copilot_chat_input_commands')
  }

  get agentTaskListV2() {
    return isFeatureEnabled('copilot_agent_task_list_v2')
  }

  get removeAssistiveChatButton() {
    return isFeatureEnabled('copilot_chat_remove_assistive_button')
  }

  get hideExtensionsInDotcomChat() {
    return isFeatureEnabled('copilot_extensions_hide_in_dotcom_chat')
  }

  get copilotSearchBarRedirect() {
    return isFeatureEnabled('copilot_chat_search_bar_redirect')
  }

  get askModeDropdown() {
    return isFeatureEnabled('copilot_ask_mode_dropdown')
  }

  get taskWithinChatThread() {
    return isFeatureEnabled('copilot_immersive_task_within_chat_thread')
  }

  get exportChat() {
    return isFeatureEnabled('copilot_chat_export')
  }

  get previewFeaturesVisionGate() {
    return isFeatureEnabled('copilot_chat_vision_preview_gate')
  }

  get codingAgentModelSelection() {
    return isFeatureEnabled('coding_agent_model_selection')
  }

  get codingAgentModelSelectionAllSkus() {
    return isFeatureEnabled('coding_agent_model_selection_all_skus')
  }

  get generateThreadNameAsync() {
    return isFeatureEnabled('copilot_immersive_generate_thread_name_async')
  }

  get copilotAgentTaskApi() {
    return isFeatureEnabled('copilot_agent_task_api')
  }

  get codingAgentProposeTasks() {
    return isFeatureEnabled('coding_agent_propose_tasks')
  }

  get copilotLinkableFilePaths() {
    return isFeatureEnabled('copilot_linkable_file_paths')
  }

  get copilotIcebreakersExperimentDashboard() {
    return isFeatureEnabled('copilot_icebreakers_experiment_dashboard')
  }

  get copilotIcebreakersExperimentHyperspace() {
    return isFeatureEnabled('copilot_icebreakers_experiment_hyperspace')
  }
}

export const copilotFeatureFlags = new CopilotFeatureFlags()
export type {CopilotFeatureFlags}
